import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { formatTimeAgo } from '../utils/formatters'

/**
 * Reusable compact sighting card component
 * Used in profile dashboards, lists, and feeds
 * 
 * @param {object} sighting - Sighting data
 * @param {function} onClick - Click handler (optional, will navigate to entity page by default)
 * @param {function} t - Translation function (optional)
 */
const SightingCard = memo(function SightingCard({ sighting, onClick, t }) {
  const navigate = useNavigate()
  
  // Normalize data from various API response formats
  const entityName = sighting.entity?.name || sighting.entity_name || sighting.entityName || sighting.name || 'Unknown Entity'
  
  // Build location display - prefer address, fall back to coordinates, then use simple label
  let locationName = sighting.address || sighting.location_name || sighting.locationName || sighting.location
  
  // If no address but we have coordinates, show lat/lng
  if (!locationName && sighting.latitude && sighting.longitude) {
    locationName = `${sighting.latitude.toFixed(4)}°, ${sighting.longitude.toFixed(4)}°`
  }
  
  // If still no location, use a simple fallback
  if (!locationName) {
    locationName = t ? t('sighting.noLocation', 'No location') : 'No location'
  }
  
  const createdAt = sighting.createdAt || sighting.created_at || sighting.timestamp || new Date().toISOString()
  const confirmCount = sighting.confirmationCount || sighting.confirmation_count || sighting.confirmations || 0
  const entityId = sighting.entityId || sighting.entity_id || sighting.entity?.id

  const handleClick = () => {
    if (onClick) {
      onClick(entityId)
    } else if (entityId) {
      navigate(`/entity/${entityId}`)
    }
  }

  return (
    <button
      onClick={handleClick}
      className="w-full text-left bg-gray-50 border border-gray-200 rounded-lg p-2.5 hover:border-primary-400 hover:bg-primary-50 transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate group-hover:text-primary-600 transition-colors">
            {entityName}
          </p>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {locationName}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-400">{formatTimeAgo(createdAt, t)}</span>
            {confirmCount > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-green-600 font-medium">
                <CheckCircleIcon className="h-3 w-3" />
                {confirmCount}
              </span>
            )}
          </div>
        </div>
        <svg className="h-4 w-4 text-gray-400 group-hover:text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  )
})

export default SightingCard
